/**
 * Shared AG Grid utilities for DataVault components
 * Consolidates common patterns to reduce code duplication
 */

/**
 * Standard text filter configuration for DataVault grids
 * Provides consistent search functionality across all entity grids
 */
export const TEXT_FILTER_DEBOUNCE_MS = 300;

export const TEXT_FILTER_OPTIONS = ["contains", "equals", "startsWith", "endsWith"];

/**
 * Creates standardized text filter configuration for AG Grid columns
 * Used across Arena and Investment Entity grids for consistent filtering
 */
export const createTextColumnFilter = () => ({
  filter: "agTextColumnFilter",
  floatingFilter: true,
  filterParams: {
    filterOptions: TEXT_FILTER_OPTIONS,
    debounceMs: TEXT_FILTER_DEBOUNCE_MS,
    maxNumConditions: 1,
    buttons: ["reset"],
    defaultOption: "contains",
  },
});

/**
 * Standard grid options configuration for DataVault grids
 * Provides consistent behavior and performance optimizations
 */
export const createBaseGridOptions = (overrides = {}) => ({
  autoSizeStrategy: {
    type: "fitGridWidth" as const,
  },
  suppressCellFocus: true,
  suppressRowHoverHighlight: false,
  domLayout: "autoHeight" as const,
  rowHeight: 42,
  defaultColDef: {
    resizable: false,
    menuTabs: ["filterMenuTab" as const],
    sortable: true,
    filter: true,
    suppressHeaderMenuButton: false,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  },
  rowSelection: "single" as const,
  suppressRowClickSelection: false,
  ...overrides,
});

/**
 * Enhanced grid options for Investment Entities Grid with pagination and performance optimizations
 */
export const createInvestmentEntitiesGridOptions = () =>
  createBaseGridOptions({
    autoSizeStrategy: {
      type: "fitGridWidth",
    },
    // Pagination configuration
    pagination: true,
    paginationPageSize: 100,
    paginationPageSizeSelector: [50, 100, 200, 500],
    suppressScrollOnNewData: true,

    // Row height configuration for text wrapping
    rowHeight: undefined, // Remove fixed row height to allow auto-height
    getRowHeight: undefined, // Use default auto-height behavior

    // Performance optimizations for large datasets
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    rowBuffer: 10,
    animateRows: false,
    deltaSort: true,
    suppressPropertyNamesCheck: true,
  });
